'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress'; 
import getPORequestColumns from '../../utils/getPORequestColumns';
import useUserMap from '../../hooks/useUserMap';
import HistoricalPoModal from './Modals/HistoricalPoModal';
import ConfirmDialog from '../UserDialog/ConfirmDialog';
import { useNetSuiteProductionRfq } from '../../config/netsuite';
import { useAuth } from '../../context/AuthContext';
import { ProcurementAdminPermissions } from '../../utils/permissions';
import axios from 'axios';

import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
]);

const calculateVendorColWidth = (data) => {
  if (!Array.isArray(data)) return 300;

  const maxLength = data.reduce((max, row) => {
    const vendors = row.appliedVendors || [];
    const longestVendor = vendors.reduce((vMax, v) => Math.max(vMax, v?.length || 0), 0);
    return Math.max(max, longestVendor);
  }, 0);

  // Estimate ~9px per character, clamp between 150 and 600
  return Math.min(600, Math.max(150, Math.ceil(maxLength * 7 + 100)));
};

const calculateProjectColWidth = (data) => {
  if (!Array.isArray(data)) return 300;

  const maxLength = data.reduce((max, row) => {
    const list = row.projectSubsidiaryNameList || [];
    const longest = list.reduce((vMax, v) => Math.max(vMax, v?.length || 0), 0);
    return Math.max(max, longest);
  }, 0);

  // Moderately conservative: ~10.5px per char + 100px buffer
  return Math.min(1000, Math.max(200, Math.ceil(maxLength * 7 + 100)));
};

const AwardedRequests = ({ data, refetch }) => {
  const [gridApi, setGridApi] = useState(null);
  const [rowData, setRowData] = useState([]);
  const userMap = useUserMap();
  const [poDataLoading, setPoDataLoading] = useState(false);
  const [historicalPoData, setHistoricalPoData] = useState([]);
  const [historicalModalOpen, setHistoricalModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const { userPermissions, permsLoading } = useAuth();

  if (permsLoading) {
    return (
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
					<Typography variant="h6">Loading User Permissions</Typography>
      </Box>
    );
  }

  const isProcurementAdmin = useMemo(
    () =>
      userPermissions.some((perm) =>
        ProcurementAdminPermissions.includes(perm)
      ),
    [userPermissions]
  );

  useEffect(() => {
    setRowData(data || []);
  }, [data]);

  const handleViewPO = async (rfqRow) => {
    setPoDataLoading(true);
    setHistoricalModalOpen(true);
    try {
      const { id: rfqId } = rfqRow;
      const bidSheetItems = await axios.get(`/api/VendorAssignedRfq/RfqDetails/Dataset/${rfqId}`);
      const uniquePoIds = [...new Set(bidSheetItems.data.map((i) => i.netSuitePoId).filter(Boolean))];

      const poDataArray = await Promise.all(
        uniquePoIds.map((poId) =>
          axios
            .get(`/api/NetSuite/PurchaseOrder`, {
              params: { puchaseOrderId: poId, useProduction: useNetSuiteProductionRfq },
            })
            .then((r) => r.data)
        )
      );

      setHistoricalPoData(poDataArray);
      setHistoricalModalOpen(true);
    } catch (err) {
      const errorMessage = err?.response?.data?.toString().toLowerCase() || '';
      if (errorMessage.includes('workflow')) {
        console.error('One or more POs currently in workflow', err);
        toast.error('Could not load one or more POs because they are locked in a Netsuite Workflow.');
      } else {
        console.error('Failed to fetch historical PO data', err);
        toast.error('Could not load one or more POs. Check Console.');
      }
    } finally {
      setPoDataLoading(false);
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false });
  };

  const handleDeleteRfq = (rfqRow) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to archive "${rfqRow.name}"?`,
      onConfirm: async () => {
        try {
          await axios.put(`/api/RfqHeader/rfq-isdelete`, {
            id: rfqRow.id,
            isDeleted: true,
          });

          toast.success('Request archived successfully');
          await refetch();
        } catch {
          console.error(err);
          if (err.response?.status === 403) {
            toast.error('Elevated permissions required for this action.');
          } else {
            toast.error('Archive failed');
          }
        } finally {
          closeConfirmDialog();
        }
      },
      onCancel: closeConfirmDialog,
    });
  };

  const actionsRenderer = (params) => (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%" paddingY={0.5} gap={1}>
      <Tooltip title="View NetSuite PO">
        <IconButton size="small" color="primary" onClick={() => handleViewPO(params.data)}>
          <VisibilityIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Archive Request">
        <IconButton size="small" color="error" onClick={() => handleDeleteRfq(params.data)}>
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const vendorColWidth = useMemo(() => calculateVendorColWidth(rowData), [rowData]);
  const projectColWidth = useMemo(() => calculateProjectColWidth(rowData), [rowData]);

  const columnDefs = useMemo(
    () =>
      getPORequestColumns(
        userMap,
        actionsRenderer,
        vendorColWidth,
        projectColWidth,
        isProcurementAdmin,
        async (id, newNotes, oldNotes) => {
          try {
            await axios.put('/api/RfqHeader/update-approver-notes', { id, approverNotes: newNotes });
            toast.success('Approver notes saved');
            await refetch();
          } catch {
            toast.error('Approver notes save failed');
            gridApi.getRowNode(id).setDataValue('approverNotes', oldNotes);
          }
        }
      ),
    [userMap, actionsRenderer, vendorColWidth, projectColWidth, isProcurementAdmin]
  );

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: {
      textAlign: 'left',
    },
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        padding: 2,
      }}
    >
      <>
        {/* Grid Section */}
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <div style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              columnDefs={columnDefs}
              rowGroupPanelShow={'always'}
              defaultColDef={defaultColDef}
              rowData={rowData}
              onGridReady={onGridReady}
            />
          </div>
        </Box>
      </>

      {/* Modals */}
      {historicalModalOpen && (
        <HistoricalPoModal
          open={historicalModalOpen}
          onClose={() => setHistoricalModalOpen(false)}
          poData={historicalPoData}
          loading={poDataLoading}
        />
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        onClose={closeConfirmDialog}
      />
    </Box>
  );
};

export default AwardedRequests;
